import React, { useRef, useState } from "react";

const ModalWrapper = ({ open, title, children, onClose, size = "modal-lg" }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className={`modal-dialog ${size} modal-dialog-centered`} role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

const TODOS_LOS_CAMPOS = [
  { campo: "anio",              label: "Año" },
  { campo: "numero_promocion",  label: "N° Promoción" },
  { campo: "ciclo_codigo",      label: "Código de Ciclo" },
  { campo: "centro_alias",      label: "Centro" },
  { campo: "centro_codigo_mdi", label: "Código MDI del Centro" },
  { campo: "curso_codigo",      label: "Código del Curso" },
];

const EJEMPLO = {
  anio:              "2026",
  numero_promocion:  "0001",
  ciclo_codigo:      "MDI-DRSP",
  centro_alias:      "CENDCAP",
  centro_codigo_mdi: "M75",
  curso_codigo:      "N1",
};

function buildPreview(campos, separador) {
  const sep = separador || "-";
  return campos
    .map((c) => EJEMPLO[c.campo] || c.campo)
    .filter(Boolean)
    .join(sep);
}

export default function PlantillaFormModal({
  open,
  title,
  form,
  setForm,
  saving,
  onClose,
  onSubmit,
  centros = [],
  centrosLoading = false,
}) {
  const dragSrcIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Mapa campo → label canónico (fuente de verdad local)
  const LABEL_MAP = Object.fromEntries(TODOS_LOS_CAMPOS.map((c) => [c.campo, c.label]));

  // Campos activos en el formato (normalizar labels por si vienen del DB con texto viejo)
  const camposActivos = (form.campos || []).map((c) => ({
    ...c,
    label: LABEL_MAP[c.campo] ?? c.label,
  }));
  // Campos disponibles = los que aún no están en camposActivos
  const camposDisponibles = TODOS_LOS_CAMPOS.filter(
    (c) => !camposActivos.some((a) => a.campo === c.campo)
  );

  const preview = buildPreview(camposActivos, form.separador);

  // --- Handlers de drag sobre la lista activa (reordenar) ---
  const handleDragStart = (e, index) => {
    dragSrcIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    const srcIndex = dragSrcIndexRef.current;
    if (srcIndex === null || srcIndex === dropIndex) return;
    const next = [...camposActivos];
    const [moved] = next.splice(srcIndex, 1);
    next.splice(dropIndex, 0, moved);
    setForm((prev) => ({ ...prev, campos: next.map((c, i) => ({ ...c, orden: i + 1 })) }));
    dragSrcIndexRef.current = null;
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
    dragSrcIndexRef.current = null;
  };

  // --- Agregar token desde disponibles ---
  const agregarCampo = (campo) => {
    const nuevo = { campo: campo.campo, label: campo.label, orden: camposActivos.length + 1 };
    setForm((prev) => ({ ...prev, campos: [...(prev.campos || []), nuevo] }));
  };

  // --- Quitar token de activos ---
  const quitarCampo = (index) => {
    const next = [...camposActivos];
    next.splice(index, 1);
    setForm((prev) => ({
      ...prev,
      campos: next.map((c, i) => ({ ...c, orden: i + 1 })),
    }));
  };

  return (
    <ModalWrapper open={open} title={title} onClose={onClose} size="modal-lg">
      <form onSubmit={onSubmit}>
        <div className="modal-body pt-0">
          <div className="row g-3 mb-3">
            <div className="col-md-7">
              <label className="form-label fw-semibold">Centro educativo</label>
              <select
                className="form-select"
                value={form.centro_id || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, centro_id: e.target.value }))}
                disabled={centrosLoading}
                required
              >
                <option value="">Selecciona un centro</option>
                {centros.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.alias || c.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Vigente desde</label>
              <input
                type="date"
                className="form-control"
                value={form.vigente_desde || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, vigente_desde: e.target.value }))}
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Separador</label>
              <input
                type="text"
                className="form-control font-monospace"
                maxLength={5}
                value={form.separador || "-"}
                onChange={(e) => setForm((prev) => ({ ...prev, separador: e.target.value }))}
                placeholder="-"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Nombre de la plantilla</label>
            <input
              type="text"
              className="form-control"
              value={form.nombre || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
              placeholder="Ej. Formato MDI 2026"
              required
            />
          </div>

          {/* Constructor de formato drag-and-drop */}
          <div className="row g-3">
            {/* Tokens disponibles */}
            <div className="col-md-5">
              <label className="form-label fw-semibold mb-1">
                <i className="bi bi-grid-3x2-gap me-1" />
                Tokens disponibles
              </label>
              <p className="text-muted small mb-2">
                Haz clic para agregar al formato.
              </p>
              <div
                className="border rounded p-2"
                style={{ minHeight: 120, background: "#f8f9fa" }}
              >
                {camposDisponibles.length === 0 ? (
                  <p className="text-muted small mb-0 text-center py-3">
                    Todos los tokens están en uso
                  </p>
                ) : (
                  camposDisponibles.map((campo) => (
                    <button
                      key={campo.campo}
                      type="button"
                      className="btn btn-outline-secondary btn-sm me-1 mb-1"
                      onClick={() => agregarCampo(campo)}
                    >
                      <i className="bi bi-plus me-1" />
                      {campo.label}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Formato activo (drag-and-drop para reordenar) */}
            <div className="col-md-7">
              <label className="form-label fw-semibold mb-1">
                <i className="bi bi-sort-down me-1" />
                Formato activo
                <span className="text-muted small fw-normal ms-1">(arrastra para reordenar)</span>
              </label>
              <div
                className="border rounded p-2"
                style={{ minHeight: 120, background: "#fff" }}
              >
                {camposActivos.length === 0 ? (
                  <p className="text-muted small mb-0 text-center py-3">
                    Agrega tokens desde la izquierda
                  </p>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {camposActivos.map((campo, index) => {
                      const isDragOver = dragOverIndex === index;
                      return (
                        <li
                          key={campo.campo}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`d-flex align-items-center gap-2 px-2 py-1 mb-1 rounded border ${
                            isDragOver ? "border-primary bg-primary-subtle" : "border-secondary-subtle bg-light"
                          }`}
                          style={{ cursor: "grab", userSelect: "none" }}
                        >
                          <i className="bi bi-grip-vertical text-muted" style={{ cursor: "grab" }} />
                          <span className="badge bg-secondary-subtle text-secondary font-monospace me-1">
                            {index + 1}
                          </span>
                          <span className="flex-grow-1 small fw-semibold">{campo.label || campo.campo}</span>
                          <span className="text-muted font-monospace small me-1">
                            {EJEMPLO[campo.campo] || campo.campo}
                          </span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0"
                            onClick={() => quitarCampo(index)}
                            title="Quitar"
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Preview del código generado */}
          {camposActivos.length > 0 && (
            <div className="mt-3 p-3 rounded border bg-dark-subtle">
              <p className="text-muted small mb-1 fw-semibold">
                <i className="bi bi-eye me-1" />
                Preview del código (con datos de ejemplo)
              </p>
              <code className="fs-6 text-dark">{preview}</code>
            </div>
          )}
        </div>

        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn management-btn-primary"
            disabled={saving || !camposActivos.length}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Guardando...
              </>
            ) : (
              "Guardar plantilla"
            )}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}
